type DateValue = Date | number | string;

export interface FormatNumberOptions extends Intl.NumberFormatOptions {
  locale?: string;
}

export interface FormatDateOptions extends Intl.DateTimeFormatOptions {
  locale?: string;
}

export interface FormatCurrencyOptions extends Intl.NumberFormatOptions {
  locale?: string;
  currency?: string;
}

const DEFAULT_LOCALE = 'en-US';

function resolveLocale(locale?: string) {
  if (locale) {
    return locale;
  }

  if (typeof navigator !== 'undefined') {
    return navigator.language;
  }

  return DEFAULT_LOCALE;
}

export function formatNumber(value: number, options: FormatNumberOptions = {}): string {
  const formatter = new Intl.NumberFormat(resolveLocale(options.locale), options);
  return formatter.format(value);
}

export function formatCurrency(value: number, options: FormatCurrencyOptions = {}): string {
  const formatter = new Intl.NumberFormat(resolveLocale(options.locale), {
    style: 'currency',
    currency: options.currency ?? 'USD',
    minimumFractionDigits: 2,
    ...options,
  });
  return formatter.format(value);
}

export function formatDate(value: DateValue, options: FormatDateOptions = {}): string {
  const date = typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
  const formatter = new Intl.DateTimeFormat(resolveLocale(options.locale), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    ...options,
  });
  return formatter.format(date);
}

/**
 * Combined date + time in en-US `MM/DD/YYYY h:mm AM/PM` shape. Useful for
 * compact "modified at" cells in tables. For other shapes, use `formatDate`
 * + a time formatter directly.
 */
export function formatDateTime(value: DateValue): string {
  const date = typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
  if (Number.isNaN(date.valueOf())) return typeof value === 'string' ? value : '';
  const datePart = date.toLocaleDateString(DEFAULT_LOCALE, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString(DEFAULT_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart} ${timePart}`;
}

export function formatRelativeTime(value: DateValue, locale?: string): string {
  const date = typeof value === 'string' || typeof value === 'number' ? new Date(value) : value;
  if (Number.isNaN(date.valueOf())) return typeof value === 'string' ? value : '';
  const formatter = new Intl.RelativeTimeFormat(resolveLocale(locale), {
    numeric: 'auto',
  });

  const diff = date.getTime() - Date.now();
  const absMs = Math.abs(diff);
  const minute = 60 * 1000;
  const hour = minute * 60;
  const day = hour * 24;
  const week = day * 7;
  const month = day * 30;
  const year = day * 365;

  if (absMs < hour) {
    return formatter.format(Math.round(diff / minute), 'minute');
  }
  if (absMs < day) {
    return formatter.format(Math.round(diff / hour), 'hour');
  }
  if (absMs < week) {
    return formatter.format(Math.round(diff / day), 'day');
  }
  if (absMs < month) {
    return formatter.format(Math.round(diff / week), 'week');
  }
  if (absMs < year) {
    return formatter.format(Math.round(diff / month), 'month');
  }
  return formatter.format(Math.round(diff / year), 'year');
}

export function formatDuration(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);

  const parts = [hours ? `${hours}h` : null, minutes ? `${minutes}m` : null, `${seconds}s`].filter(
    Boolean
  );

  return parts.join(' ');
}

export function truncateText(value: string, maxLength = 140): string {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1)}…`;
}

const SECRET_MASK = '*'.repeat(10);
const SECRET_VISIBLE_SUFFIX = 4;
const SECRET_MIN_LENGTH_FOR_SUFFIX = 12;

export function maskSecret(
  value: string | null | undefined,
  { showSuffix = true }: { showSuffix?: boolean } = {}
): string {
  if (!value) {
    return '';
  }

  if (!showSuffix || value.length < SECRET_MIN_LENGTH_FOR_SUFFIX) {
    return SECRET_MASK;
  }

  return SECRET_MASK.slice(SECRET_VISIBLE_SUFFIX) + value.slice(-SECRET_VISIBLE_SUFFIX);
}
